import { Css, FullBleed, Icon, IconButton, Properties, Typography, useTestIds, Xss } from "@homebound/beam";
import { Fragment, ReactNode, ReactPortal, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useBlueprintContext } from "src/contexts/BlueprintContext";
import { useProjectTitle } from "src/hooks/useProjectTitle";

// Not a fully flushed out component/layout for breadcrumbs. Currently only displaying more of a "Back" link.
export type HeaderBreadcrumb = { href: string; label: string; right?: ReactNode };

type PageHeaderProps = {
  // Wrapped in an `h1` tag, and used as the `document.title`. If within a Project's context the Title will be suffixed with the project name
  title?: string;
  // Allows for hiding the title
  hideTitle?: boolean;
  // Allows adjusting title font size. Typically should not need this, but can be useful for special cases.
  titleSize?: Typography;
  // Subtitle to be displayed under the title
  subTitle?: string;
  breadcrumb?: HeaderBreadcrumb | HeaderBreadcrumb[];
  // Enables collapsing the breadcrumb container when there are more than 3 items
  collapsibleBreadcrumb?: boolean;
  // Url the user should be redirected to when clicked
  backButton?: string | (() => void);
  // Additional content that will show on the left of the header, but to the right of the breadcrumb and title (if supplied).
  left?: ReactNode;
  // Additional content to show on the right side of the screen. Typically actions.
  right?: ReactNode;
  // Disable the bottom border; we used to use `xss=Css.bn.$`, but that shorthand doesn't override bb longhand. */
  noBorder?: boolean;
  // Allows for additional css to be added
  xss?: Properties;
};

/** Provides `header` element with a bottom border that extends the full width of its container */
export function PageHeader(props: PageHeaderProps) {
  const { headerActionsRef, headerActionsEl } = useBlueprintContext();
  const {
    title,
    subTitle,
    titleSize = "xl",
    hideTitle,
    breadcrumb,
    backButton,
    right,
    left,
    noBorder = false,
    xss,
    collapsibleBreadcrumb,
    ...otherProps
  } = props;
  const tid = useTestIds(otherProps, "header");
  useProjectTitle(title);

  useEffect(() => {
    if (headerActionsRef && headerActionsEl) {
      headerActionsRef.current!.appendChild(headerActionsEl);
    }
  }, [headerActionsEl, headerActionsRef]);

  return (
    <FullBleed>
      <header
        css={{
          ...Css.df.gap3.aic.py2.mhPx(64).mb3.bcGray200.bgWhite.ifMdAndDown.fdc.$,
          ...Css.if(!noBorder).bb.$,
          ...xss,
        }}
        {...tid}
      >
        {/* if title or breadcrumbs exist, then we have a left. */}
        {(breadcrumb || backButton || (title && !hideTitle) || left) && (
          <div css={Css.fs1.mra.$}>
            {breadcrumb && (
              <div css={Css.if(!!backButton).mlPx(44).$}>
                <PageHeaderBreadcrumbs breadcrumb={breadcrumb} collapsible={collapsibleBreadcrumb} />
              </div>
            )}
            <div css={Css.df.aic.$}>
              {backButton && (
                // Applying `df` to ensure wrapping element stays at same height as the IconButton.
                // This is due to <IconButton /> being an inline element, which causes the wrapping element takes `line-height` into account. See https://stackoverflow.com/questions/11126685/why-does-container-div-insist-on-being-slightly-larger-than-img-or-svg-content
                <div css={Css.df.asfs.mr2.fs0.$}>
                  <IconButton icon="arrowBack" onClick={backButton} />
                </div>
              )}
              {!hideTitle && title && (
                <h1 css={Css.typography(titleSize).mr2.$} {...tid.title}>
                  {title}
                </h1>
              )}
              <div css={Css.df.gap1.$}>{left}</div>
            </div>
            {subTitle && <div css={Css.mlPx(44).mt1.$}>{subTitle}</div>}
          </div>
        )}
        <div css={Css.df.aic.fs0.mla.$}>
          {right && <div css={Css.df.aic.fs0.gap1.$}>{right}</div>}
          <div ref={headerActionsRef} />
        </div>
      </header>
    </FullBleed>
  );
}

type PageHeaderBreadcrumbsProps = {
  breadcrumb: HeaderBreadcrumb | HeaderBreadcrumb[];
  linkXss?: Xss<"color" | "cursor">;
  collapsible?: boolean;
};

export function PageHeaderBreadcrumbs({ breadcrumb, linkXss, collapsible }: PageHeaderBreadcrumbsProps) {
  const tid = useTestIds({}, "breadcrumb");
  const breadcrumbs = useMemo(() => (Array.isArray(breadcrumb) ? breadcrumb : [breadcrumb]), [breadcrumb]);
  const [collapsed, setCollapsed] = useState(breadcrumbs.length > 3);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (breadcrumbs.isEmpty || !collapsible || collapsed) return;
    // Collapse when clicking outside of the container
    const onClick = (e: MouseEvent) =>
      containerRef.current && !containerRef.current.contains(e.target as Node) && setCollapsed(true);
    document.addEventListener("mousedown", onClick);
    // Cleanup the event listener on component unmount
    return () => document.removeEventListener("mousedown", onClick);
  }, [collapsed, collapsible, setCollapsed, breadcrumbs]);

  const renderBreadcrumb = (bc: HeaderBreadcrumb, i: number, hideDivisor?: boolean) => (
    <Fragment key={`${bc.label}-${i}`}>
      {i > 0 &&
        !hideDivisor &&
        (collapsible ? (
          <span css={Css.gray700.pxPx(4).$}>/</span>
        ) : (
          <span css={Css.mr1.ml1.$}>
            <Icon icon="chevronRight" />
          </span>
        ))}
      <Link {...tid.link} to={bc.href} css={{ ...Css.xs.gray700.onHover.gray800.$, ...linkXss }} className="navLink">
        {bc.label}
      </Link>
      {bc.right}
    </Fragment>
  );

  return (
    <div ref={containerRef} css={Css.df.aic.mbPx(4).$}>
      {breadcrumbs.length > 3 && collapsed ? (
        <>
          {renderBreadcrumb(breadcrumbs[0], 0)}
          <button {...tid.expand} css={Css.gray700.pxPx(8).$} onClick={() => setCollapsed(false)}>
            ...
          </button>
          {renderBreadcrumb(breadcrumbs[breadcrumbs.length - 2], breadcrumbs.length - 2, true)}
          {renderBreadcrumb(breadcrumbs[breadcrumbs.length - 1], breadcrumbs.length - 1)}
        </>
      ) : (
        breadcrumbs.map((bc, i) => renderBreadcrumb(bc, i))
      )}
    </div>
  );
}

/** Helper component to append content into the PageHeader via a portal */
export function PageHeaderActions(props: { children: JSX.Element }): ReactPortal | JSX.Element {
  const { headerActionsEl } = useBlueprintContext();
  const { children } = props;
  const wrappedChildren = <div css={Css.ml1.df.aic.fs0.gap1.$}>{children}</div>;
  // Fallback for testing purposes, if the headerActionsEl doesn't exist, just return the children so the tests can still find the elements they need
  if (!headerActionsEl) {
    return wrappedChildren;
  }
  return createPortal(wrappedChildren, headerActionsEl);
}
